// Pure engine helpers shared between the browser (@cesdk/cesdk-js) and the
// Node worker (@cesdk/node). Everything here touches `engine.block.*` only —
// no DOM, no canvas, no fetch. Callers pass a compatible engine handle.
//
// The browser copy in packages/ui/src/components/CreativeEditor/utils/block.ts
// re-exports from this file so historical import paths keep working. Do not
// duplicate logic there — this is the single source of truth.

import type CreativeSDK from '@cesdk/cesdk-js';
import type { CreativeEngine, ObjectTypeLonghand } from '@cesdk/cesdk-js';
import { GQLProductSideSideType } from '@theprintbar/graphql';

const NECK_LABEL_META = 'neckLabel';
const EXCLUSION_META = 'exclusion';

export const getPageSide = (editor: CreativeSDK, page: number) =>
	editor.engine.block.getName(page).split('-')[0] as GQLProductSideSideType;
export const getPageId = (side: GQLProductSideSideType) => `${side}-page`;
export const getPage = (editor: CreativeSDK, side: GQLProductSideSideType) =>
	editor.engine.block.findByName(getPageId(side))?.[0];

export const isPageBlock = (editor: CreativeSDK, block: number) => editor.engine.block.getName(block).includes('page');

export const isBackgroundBlock = (editor: CreativeSDK, block: number) => editor.engine.block.getName(block).includes('background');
export const getBackgroundId = (side: GQLProductSideSideType) => `${side}-background`;
export const getBackground = (editor: CreativeSDK, side: GQLProductSideSideType) =>
	editor.engine.block.findByName(getBackgroundId(side))?.[0];

export function isTextBlock(type: ObjectTypeLonghand) {
	return type === '//ly.img.ubq/text';
}

export function isGraphicBlock(type: ObjectTypeLonghand) {
	return type === '//ly.img.ubq/graphic';
}

export function isFillBlock(type: ObjectTypeLonghand) {
	return type === '//ly.img.ubq/fill/color';
}

export function isGraphicOrTextBlock(type: ObjectTypeLonghand) {
	return isGraphicBlock(type) || isTextBlock(type);
}

export function isBlockWithFill(editor: CreativeSDK, block: number) {
	try {
		const type = editor.engine.block.getType(block);
		return isGraphicOrTextBlock(type) || isFillBlock(type) || !isBackgroundBlock(editor, block);
	} catch (e) {
		return false;
	}
}

export function isImageBlock(type: ObjectTypeLonghand, editor: CreativeSDK, block: number) {
	try {
		if (!isGraphicBlock(type)) return false;
		const fill = editor.engine.block.getFill(block);
		const props = editor.engine.block.findAllProperties(fill);
		return props.includes('fill/image/sourceSet');
	} catch (e) {
		return false;
	}
}

export async function getBlockMimeType(editor: CreativeSDK, block: number) {
	if (!isImageBlock(editor.engine.block.getType(block), editor, block)) {
		return null;
	}
	try {
		const fill = editor.engine.block.getFill(block);
		const props = editor.engine.block.findAllProperties(fill);
		if (!props.includes('fill/image/imageFileURI')) {
			return null;
		}
		const file = editor.engine.block.getString(fill, 'fill/image/imageFileURI');
		const mimeType = await editor.engine.editor.getMimeType(file);
		return mimeType;
	} catch (e) {
		return null;
	}
}

export type BoundingBox = ReturnType<typeof getBoundingBox>;
export function getBoundingBox(engine: CreativeEngine, block: number) {
	const { block: blockEngine } = engine;
	const blockX = blockEngine.getGlobalBoundingBoxX(block);
	const blockY = blockEngine.getGlobalBoundingBoxY(block);
	const blockWidth = blockEngine.getGlobalBoundingBoxWidth(block) || blockEngine.getFrameWidth(block);
	const blockHeight = blockEngine.getGlobalBoundingBoxHeight(block) || blockEngine.getFrameHeight(block);

	return {
		left: blockX,
		right: blockX + blockWidth,
		top: blockY,
		bottom: blockY + blockHeight,
		width: blockWidth,
		height: blockHeight,
	};
}

export function boundingBoxesOverlap(main: BoundingBox, secondary: BoundingBox) {
	return main.left < secondary.right && main.right > secondary.left && main.top < secondary.bottom && main.bottom > secondary.top;
}

export function getSelectionBoundingBox(engine: CreativeEngine, blocks?: number[]) {
	const selectedBlocks = blocks || engine.block.findAllSelected();
	if (!selectedBlocks.length) {
		return {
			left: 0,
			right: 0,
			top: 0,
			bottom: 0,
			width: 0,
			height: 0,
		};
	}

	const boundingBoxes = selectedBlocks.map(block => getBoundingBox(engine, block));

	if (boundingBoxes.length === 1) {
		return boundingBoxes[0];
	}

	const sides = {
		left: Math.min(...boundingBoxes.map(box => box.left)),
		right: Math.max(...boundingBoxes.map(box => box.right)),
		top: Math.min(...boundingBoxes.map(box => box.top)),
		bottom: Math.max(...boundingBoxes.map(box => box.bottom)),
	};

	return {
		...sides,
		width: sides.right - sides.left,
		height: sides.bottom - sides.top,
	};
}

export function getOverlappingBoundingBox(
	editor: CreativeSDK,
	blocks: number[],
	page: number,
): { box: BoundingBox; hasCutoff: boolean } | null {
	const pageBoundingBox = getBoundingBox(editor.engine, page);
	const blockBoundingBox = getSelectionBoundingBox(editor.engine, blocks);

	//This means the blocks are inside the page
	if (boundingBoxesOverlap(pageBoundingBox, blockBoundingBox)) {
		//Return the overlap in page-local coords (0,0 = page top-left). Pages after the
		//first are auto laid out horizontally by CE.SDK, so leaving these in global scene
		//coords would carry the back page's scene X/Y offset downstream.
		const sides = {
			left: Math.max(pageBoundingBox.left, blockBoundingBox.left) - pageBoundingBox.left,
			right: Math.min(pageBoundingBox.right, blockBoundingBox.right) - pageBoundingBox.left,
			top: Math.max(pageBoundingBox.top, blockBoundingBox.top) - pageBoundingBox.top,
			bottom: Math.min(pageBoundingBox.bottom, blockBoundingBox.bottom) - pageBoundingBox.top,
		};

		const result: BoundingBox = {
			...sides,
			width: sides.right - sides.left,
			height: sides.bottom - sides.top,
		};

		return {
			box: result,
			hasCutoff: result.width < blockBoundingBox.width || result.height < blockBoundingBox.height,
		};
	} else {
		return null;
	}
}

// Metadata-tagged blocks — matched by the string set at creation time. Kept
// here so both worlds identify them identically; the browser-side creation
// code still lives with the rest of the editor tooling.
export function isNeckLabelBlock(block: number, editor: CreativeSDK): boolean {
	return editor.engine.block.hasMetadata(block, NECK_LABEL_META);
}

export function isExclusionBlock(block: number, editor: CreativeSDK): boolean {
	return editor.engine.block.hasMetadata(block, EXCLUSION_META);
}

export function getUserAddedBlocksOnPage(editor: CreativeSDK, page: number, includeLabels = false) {
	return editor.engine.block.getChildren(page).filter(block => {
		const visible = editor.engine.block.isVisible(block);
		const selectable = editor.engine.block.isScopeEnabled(block, 'editor/select');
		return visible && (selectable || (includeLabels && isNeckLabelBlock(block, editor)));
	});
}

export function getUserAddedBlocks(editor: Pick<CreativeSDK, 'engine'>) {
	return editor.engine.block.findAll().filter(b => isUserAddedBlock(editor, b));
}

export function isUserAddedBlock(editor: Pick<CreativeSDK, 'engine'>, block: number) {
	try {
		const visible = editor.engine.block.isVisible(block);
		const selectable = editor.engine.block.isScopeEnabled(block, 'editor/select');
		return visible && selectable;
	} catch (e) {
		return false;
	}
}
